// @ts-nocheck
/**
 * chat-reply.js — Reply-to-bubble state and interactions.
 *
 * Handles:
 * - Mobile swipe-right to reply
 * - Desktop chevron click to reply
 * - Composer reply chip render/clear
 *
 * Keeps gesture/state logic out of render/controller modules.
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};

  var messagesEl = null;
  var replySlotEl = null;
  var inputEl = null;
  var workerUrl = '';
  var activeReplyTarget = null;
  var SWIPE_START_PX = 8;
  var SWIPE_TRIGGER_PX = 52;
  var SWIPE_MAX_DRAG_PX = 52;
  var SWIPE_VERTICAL_TOLERANCE_PX = 26;

  function init(deps) {
    messagesEl = deps.messagesEl;
    replySlotEl = deps.replySlotEl;
    inputEl = deps.inputEl;
    workerUrl = String(deps.workerUrl || '');
    bindDesktopReplyClick();
    bindMobileSwipeReply();
    renderComposerReplyChip();
  }

  function bindDesktopReplyClick() {
    if (!messagesEl) return;
    messagesEl.addEventListener('click', function (event) {
      var btn = event.target.closest('.afc-reply-btn, .afc-image-reply-btn');
      if (!btn) return;
      var msgEl = btn.closest('.afc-msg');
      if (!msgEl) return;
      var target = buildTargetFromMessageEl(msgEl, btn.closest('.afc-image-card'));
      if (!target) return;
      setTarget(target);
    });
  }

  function bindMobileSwipeReply() {
    if (!messagesEl) return;
    var tracking = null;

    messagesEl.addEventListener('touchstart', function (event) {
      var touch = event.touches && event.touches[0];
      if (!touch) return;
      if (event.target.closest('.afc-variation-row')) return;
      var msgEl = event.target.closest('.afc-msg');
      if (!msgEl || msgEl.classList.contains('afc-msg--system')) return;
      tracking = {
        msgEl: msgEl,
        startX: touch.clientX,
        startY: touch.clientY,
        lockedAxis: null,
        canTriggerOnRelease: false,
        dragX: 0,
      };
    }, { passive: true });

    messagesEl.addEventListener('touchmove', function (event) {
      if (!tracking) return;
      var touch = event.touches && event.touches[0];
      if (!touch) return;
      var dx = touch.clientX - tracking.startX;
      var dy = touch.clientY - tracking.startY;
      var absDx = Math.abs(dx);
      var absDy = Math.abs(dy);

      if (!tracking.lockedAxis) {
        if (absDx < SWIPE_START_PX && absDy < SWIPE_START_PX) return;
        tracking.lockedAxis = absDx > absDy ? 'x' : 'y';
      }
      if (tracking.lockedAxis !== 'x') return;
      if (absDy > SWIPE_VERTICAL_TOLERANCE_PX) {
        tracking.canTriggerOnRelease = false;
        return;
      }
      event.preventDefault();

      var dragX = Math.max(0, Math.min(dx, SWIPE_MAX_DRAG_PX));
      tracking.dragX = dragX;
      applyDrag(tracking.msgEl, dragX, false);
      tracking.canTriggerOnRelease = dragX >= SWIPE_TRIGGER_PX;
    }, { passive: false });

    messagesEl.addEventListener('touchend', function () {
      if (tracking && tracking.canTriggerOnRelease) {
        var target = buildTargetFromMessageEl(tracking.msgEl);
        if (target) {
          setTarget(target);
          triggerHapticFeedback();
        }
      }
      if (tracking && tracking.msgEl) resetDrag(tracking.msgEl);
      tracking = null;
    }, { passive: true });

    messagesEl.addEventListener('touchcancel', function () {
      if (tracking && tracking.msgEl) resetDrag(tracking.msgEl);
      tracking = null;
    }, { passive: true });
  }

  function buildTargetFromMessageEl(msgEl, imageCard) {
    if (!msgEl) return null;
    var role = msgEl.getAttribute('data-role');
    var messageId = msgEl.getAttribute('data-msg-id');
    var snippet = msgEl.getAttribute('data-reply-snippet');
    if (!messageId || !snippet || (role !== 'user' && role !== 'assistant')) return null;
    var cardImageUrl = imageCard && imageCard.getAttribute('data-variation-image-url');
    var cardName = imageCard && imageCard.getAttribute('data-variation-arrangement-name');
    return {
      messageId: messageId,
      role: role,
      snippet: cardName || snippet,
      hasImage: msgEl.getAttribute('data-has-image') === 'true',
      imageUrl: cardImageUrl || msgEl.getAttribute('data-reply-image-url') || undefined,
      timestamp: Number(msgEl.getAttribute('data-msg-ts') || 0) || undefined,
    };
  }

  function setTarget(target) {
    activeReplyTarget = target || null;
    renderComposerReplyChip();
    var isMobile = window.matchMedia('(pointer: coarse)').matches || window.innerWidth <= 768;
    if (inputEl && !isMobile) inputEl.focus();
  }

  function clearTarget() {
    activeReplyTarget = null;
    renderComposerReplyChip();
  }

  function getTarget() {
    return activeReplyTarget ? {
      messageId: activeReplyTarget.messageId,
      role: activeReplyTarget.role,
      snippet: activeReplyTarget.snippet,
      hasImage: !!activeReplyTarget.hasImage,
      imageUrl: activeReplyTarget.imageUrl,
      timestamp: activeReplyTarget.timestamp,
    } : null;
  }

  function renderComposerReplyChip() {
    if (!replySlotEl) return;
    if (!activeReplyTarget) {
      replySlotEl.innerHTML = '';
      replySlotEl.style.display = 'none';
      return;
    }
    replySlotEl.style.display = 'block';
    var roleLabel = activeReplyTarget.role === 'assistant' ? 'Fleur' : 'You';
    var chipLabel = activeReplyTarget.imageUrl
      ? 'Replying to image'
      : ('Replying to ' + roleLabel);
    var thumbHtml = '';
    if (activeReplyTarget.imageUrl) {
      var thumbSrc = toDisplayImageUrl(activeReplyTarget.imageUrl);
      if (thumbSrc) {
        thumbHtml =
          '<img class="afc-reply-chip-thumb" src="' + escapeHtml(thumbSrc) + '" alt="Referenced image thumbnail" loading="lazy" />';
      }
    }
    replySlotEl.innerHTML =
      '<div class="afc-reply-chip">' +
        thumbHtml +
        '<div class="afc-reply-chip-label">' + escapeHtml(chipLabel) + '</div>' +
        '<div class="afc-reply-chip-snippet">' + renderSnippetMarkdown(activeReplyTarget.snippet) + '</div>' +
        '<button type="button" class="afc-reply-chip-clear" aria-label="Clear reply">&times;</button>' +
      '</div>';
    var clearBtn = replySlotEl.querySelector('.afc-reply-chip-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        clearTarget();
      });
    }
  }

  function toDisplayImageUrl(imageUrl) {
    var raw = String(imageUrl || '').trim();
    if (!raw) return '';
    var base = resolveWorkerBase();
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.charAt(0) === '/') {
      return base ? (base + raw) : raw;
    }
    return base ? (base + '/images/' + raw.replace(/^images\//, '')) : raw;
  }

  function resolveWorkerBase() {
    if (workerUrl) return workerUrl;
    try {
      var root = document.getElementById('aiflorist-chat-root');
      var configured = root && root.dataset ? String(root.dataset.workerUrl || '').trim() : '';
      if (configured) return configured;
    } catch (_err) {
      // no-op, use fallback
    }
    var host = (window.location && window.location.hostname) || '';
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:8795';
    }
    return '';
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderSnippetMarkdown(str) {
    var escaped = escapeHtml(str || '');
    // Support lightweight markdown bold in reply chips: **text**
    return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  }

  function applyDrag(msgEl, x, snap) {
    if (!msgEl) return;
    msgEl.style.transform = 'translateX(' + x + 'px)';
    msgEl.style.transition = snap ? 'transform 80ms ease-out' : 'none';
  }

  function resetDrag(msgEl) {
    if (!msgEl) return;
    msgEl.style.transition = 'transform 160ms ease-out';
    msgEl.style.transform = 'translateX(0px)';
    setTimeout(function () {
      if (!msgEl) return;
      msgEl.style.transition = '';
    }, 180);
  }

  function triggerHapticFeedback() {
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(10);
      }
    } catch (_err) {
      // Best-effort only (unsupported on some browsers/devices).
    }
  }

  window.AIFlorist.reply = {
    init: init,
    setTarget: setTarget,
    clearTarget: clearTarget,
    getTarget: getTarget,
    renderComposerReplyChip: renderComposerReplyChip,
  };
})();
